import { useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg, fmtDate } from "@/lib/api";
import { useAdminApi, saveBlob } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";
import { StatusBadge } from "@/components/site/Notices";
import { AppDetail } from "@/components/admin/AppDetail";

const STATUSES = ["draft", "payment_pending", "submitted", "under_review", "shortlisted", "interview_scheduled", "selected", "rejected", "withdrawn"];

export default function Applications() {
  const call = useAdminApi();
  const [f, setF] = useState({ q: "", job_id: "", district: "", status: "", payment_status: "", date_from: "", date_to: "", page: 1 });
  const [applied, setApplied] = useState(f);
  const [data, reload] = useLoad("/admin/applications", applied);
  const [jobs] = useLoad("/admin/jobs");
  const [open, setOpen] = useState(null);
  const set = (k) => (v) => setF({ ...f, [k]: v?.target ? v.target.value : v });

  const exportCsv = async () => {
    try {
      const blob = await call("get", "/admin/applications/export.csv", undefined, { params: applied, responseType: "blob" });
      saveBlob(blob, "applications.csv");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <PageHead title="Applications">
        <Button variant="outline" onClick={exportCsv} data-testid="applications-export-csv"><Download className="h-4 w-4" /> Export CSV (Excel)</Button>
      </PageHead>
      <div className="mb-4 flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-white p-4">
        <Input className="h-9 w-64" placeholder="Name, phone, email or reference" value={f.q} onChange={set("q")} data-testid="applications-search-input" />
        <NativeSelect value={f.job_id} onChange={set("job_id")} placeholder="All positions" options={(jobs || []).map((j) => [j.id, j.title])} testid="applications-filter-job" />
        <Input className="h-9 w-36" placeholder="District" value={f.district} onChange={set("district")} data-testid="applications-filter-district" />
        <NativeSelect value={f.status} onChange={set("status")} placeholder="All statuses" options={STATUSES} testid="applications-filter-status" />
        <NativeSelect value={f.payment_status} onChange={set("payment_status")} placeholder="Any payment" options={["paid", "pending", "unpaid"]} testid="applications-filter-payment" />
        <Input type="date" className="h-9 w-40" value={f.date_from} onChange={set("date_from")} data-testid="applications-filter-from" />
        <Input type="date" className="h-9 w-40" value={f.date_to} onChange={set("date_to")} data-testid="applications-filter-to" />
        <Button className="h-9" onClick={() => setApplied({ ...f, page: 1 })} data-testid="applications-apply-filters">Search</Button>
      </div>
      <p className="mb-2 text-sm text-slate-500" data-testid="applications-total">{data?.total ?? 0} application(s)</p>
      <DataTable head={["Reference", "Applicant", "Position", "District", "Created", "Payment", "Status"]} testid="applications-table">
        {data?.items.map((a) => (
          <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setOpen(a.id)} data-testid={`application-row-${a.reference}`}>
            <Td className="font-mono-num text-xs">{a.reference}</Td>
            <Td><p className="font-semibold">{a.personal.full_name}</p><p className="text-xs text-slate-500">{a.personal.mobile} · {a.personal.email}</p><div className="mt-1.5 flex flex-wrap gap-1 md:hidden" data-testid={`application-row-badges-${a.reference}`}><StatusBadge status={a.payment_status} /><StatusBadge status={a.status} /></div></Td>
            <Td>{a.job_title}</Td><Td>{a.personal.district}</Td><Td>{fmtDate(a.created_at)}</Td>
            <Td><StatusBadge status={a.payment_status} /></Td><Td><StatusBadge status={a.status} /></Td>
          </tr>
        ))}
      </DataTable>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" disabled={applied.page <= 1} onClick={() => setApplied({ ...applied, page: applied.page - 1 })} data-testid="applications-prev-page">Previous</Button>
        <Button variant="outline" size="sm" disabled={!data || applied.page * 25 >= data.total} onClick={() => setApplied({ ...applied, page: applied.page + 1 })} data-testid="applications-next-page">Next</Button>
      </div>
      {open && <AppDetail id={open} onClose={() => { setOpen(null); reload(); }} />}
    </div>
  );
}
